<template>
    <base-dialog :loading="loading" :value="dialog" :title="$t('business.quick_charge')" :width="960" :fullscreen="isMobile"
        @input="onInput" @close="hide">
        <div class="quick-bar">
            <kimsa-text-field class="quick-search" :value="search" :placeholder="$t('common.search')"
                prepend-inner-icon="mdi-magnify" dense @change="search = $event" />

            <div class="quick-year">
                <button-tooltip top :tooltip="$t('business.previous_year')">
                    <template v-slot:activator="{ on, attrs }">
                        <button type="button" class="icon-button" v-bind="attrs" v-on="on" @click="moveYear(-1)">
                            <v-icon size="15">mdi-chevron-left</v-icon>
                        </button>
                    </template>
                </button-tooltip>

                <span class="quick-year-label">{{ year }}</span>

                <button-tooltip top :tooltip="$t('business.next_year')">
                    <template v-slot:activator="{ on, attrs }">
                        <button type="button" class="icon-button" v-bind="attrs" v-on="on" @click="moveYear(1)">
                            <v-icon size="15">mdi-chevron-right</v-icon>
                        </button>
                    </template>
                </button-tooltip>
            </div>

            <div class="quick-legend">
                <span class="legend-item"><span class="legend-dot legend-dot--paid" />{{ $t('business.paid') }}</span>
                <span class="legend-item"><span class="legend-dot legend-dot--due" />{{ $t('business.unpaid') }}</span>
            </div>
        </div>

        <skeleton-loader v-if="loading" type="table" />

        <div v-else class="quick-scroll">
            <div class="quick-grid" :style="gridStyle">
                <span class="quick-head quick-head--client">{{ $t('business.client') }}</span>
                <span v-for="month in months" :key="`h${month.value}`" class="quick-head"
                    :class="{ 'quick-head--current': isCurrent(month) }">{{ month.label }}</span>

                <template v-for="client in filtered">
                    <span :key="`n${client.key}`" class="quick-client">
                        <span class="quick-avatar" :style="client.avatarGradient">{{ client.initials }}</span>
                        <span class="quick-client-text">
                            <truncated-text class="quick-client-name" :text="client.name" />
                            <span class="quick-client-plan">{{ planLabel(client) }}</span>
                        </span>
                    </span>
                    <button v-for="month in months" :key="`${client.key}-${month.value}`" type="button"
                        class="quick-cell" :class="`quick-cell--${cellState(client, month)}`"
                        @click="onCell(client, month)">
                        <template v-if="cellState(client, month) === 'paid'">
                            <span class="cell-icon">✓</span>
                            <span class="cell-amount">{{ formatMoney(paymentOf(client, month).amount) }}</span>
                            <span class="cell-method">{{ methodLabel(paymentOf(client, month).method) }}</span>
                        </template>
                        <template v-else-if="cellState(client, month) === 'overdue'">
                            <span class="cell-icon">✕</span>
                            <span class="cell-amount">{{ client.planAmount ? formatMoney(client.planAmount) : '' }}</span>
                        </template>
                        <span v-else-if="cellState(client, month) === 'empty'" class="cell-icon cell-icon--add">+</span>
                        <span v-else class="cell-icon cell-icon--muted">·</span>
                    </button>
                </template>

                <span class="quick-total quick-total--label">{{ $t('business.month_total') }}</span>
                <span v-for="month in months" :key="`t${month.value}`" class="quick-total">
                    <strong>{{ monthTotal(month).amount ? formatMoney(monthTotal(month).amount) : '—' }}</strong>
                    <small v-if="monthTotal(month).count">
                        {{ $tc('business.payments_count', monthTotal(month).count, { n: monthTotal(month).count }) }}
                    </small>
                </span>
            </div>

            <div v-if="filtered.length === 0" class="quick-empty">{{ $t('business.no_clients_filter') }}</div>
            <div v-else-if="truncatedLabel" class="quick-truncated">{{ truncatedLabel }}</div>
        </div>

        <template v-slot:footer>
            <dialog-actions hide-cancel :confirm-label="$t('common.close')" @confirm="hide" />
        </template>
    </base-dialog>
</template>

<script>
import BaseDialog from "@/components/common/BaseDialog";
import DialogActions from "@/components/common/DialogActions";
import SkeletonLoader from "@/components/common/SkeletonLoader";
import ButtonTooltip from "@/components/common/ButtonTooltip";
import TruncatedText from "@/components/common/TruncatedText";
import KimsaTextField from "@/components/common/inputs/KimsaTextField";
import { getAvatarGradient, getInitials, safeLower } from "@/utils/Common";
import { formatMoney, loadYearPayments, paymentDocId } from "@/utils/Business";

const MAX_ROWS = 60

export default {
    name: "QuickChargeDialog",
    components: { BaseDialog, DialogActions, SkeletonLoader, ButtonTooltip, TruncatedText, KimsaTextField },
    props: {
        companyId: {
            type: String,
            default: null,
        },
        clients: {
            type: Array,
            default: () => [],
        },
    },
    data() {
        return {
            dialog: false,
            loading: true,
            year: Number(this.$moment().format('YYYY')),
            search: '',
            payments: {},
        }
    },
    computed: {
        months(vm = this) {
            void vm.$i18n.locale
            return Array.from({ length: 12 }, (_, index) => ({
                value: String(index + 1).padStart(2, '0'),
                label: vm.$moment().month(index).format('MMM'),
            }))
        },
        gridStyle(vm = this) {
            return { gridTemplateColumns: `minmax(160px, 1.4fr) repeat(${vm.months.length}, minmax(58px, 1fr))` }
        },
        matching(vm = this) {
            const search = safeLower(vm.search).trim()
            const rows = vm.clients.map(client => ({
                ...client,
                key: client.id || client.studentId,
                initials: getInitials(client.name),
                avatarGradient: getAvatarGradient(client.studentId || client.id || client.name),
            }))
            if (!search) return rows
            return rows.filter(client => safeLower(client.name).includes(search)
                || safeLower(client.email).includes(search))
        },
        // 12 celdas por fila: sin tope, un roster grande hace lenta la apertura.
        filtered(vm = this) {
            return vm.matching.slice(0, MAX_ROWS)
        },
        truncatedLabel(vm = this) {
            if (vm.matching.length <= MAX_ROWS) return ''
            return vm.$t('business.quick_charge_truncated', { shown: MAX_ROWS, total: vm.matching.length })
        },
        currentPeriod(vm = this) {
            return vm.$moment().format('YYYY-MM')
        },
    },
    methods: {
        formatMoney,
        async show() {
            const vm = this
            vm.dialog = true
            await vm.load()
        },
        hide() {
            this.dialog = false
        },
        onInput(value) {
            if (!value) this.hide()
        },
        async load() {
            const vm = this
            if (!vm.companyId) return
            vm.loading = true
            try {
                const list = await loadYearPayments(vm.companyId, vm.year)
                const map = {}
                list.forEach(payment => { map[payment.id] = payment })
                vm.payments = map
            } catch (error) {
                console.error('[business] loadYearPayments', error?.code, error?.message)
                vm.toast(vm.$t('common.error_generic'), 'error')
            } finally {
                vm.loading = false
            }
        },
        moveYear(direction) {
            const vm = this
            vm.year += direction
            vm.load()
        },
        paymentOf(client, month) {
            const vm = this
            if (!client.id) return null
            return vm.payments[paymentDocId(vm.companyId, `${vm.year}-${month.value}`, client.id)] || null
        },
        isCurrent(month) {
            return `${this.year}-${month.value}` === this.currentPeriod
        },
        // paid: cobrado · overdue: mes cumplido sin pago · empty: mes sin cobrar aun
        // (el alumno ya existia) · future: posterior a hoy o anterior al alta.
        cellState(client, month) {
            const vm = this
            const period = `${vm.year}-${month.value}`
            if (vm.paymentOf(client, month)) return 'paid'
            if (period > vm.currentPeriod) return 'future'
            const joined = (client.joinedAt || '').slice(0, 7)
            if (joined && period < joined) return 'future'
            return period === vm.currentPeriod ? 'empty' : 'overdue'
        },
        onCell(client, month) {
            const vm = this
            if (vm.cellState(client, month) === 'future') return
            vm.$emit('cell', { client, period: `${vm.year}-${month.value}` })
        },
        planLabel(client) {
            const vm = this
            if (!client.planAmount) return vm.$t('business.no_plan_amount')
            return `${formatMoney(client.planAmount)}${vm.$t('business.per_month_short')}`
        },
        methodLabel(method) {
            return method ? this.$t(`business.method_short_${method}`) : ''
        },
        monthTotal(month) {
            const vm = this
            let amount = 0
            let count = 0
            vm.filtered.forEach(client => {
                const payment = vm.paymentOf(client, month)
                if (!payment) return
                amount += payment.amount
                count += 1
            })
            return { amount, count }
        },
    },
}
</script>

<style scoped>
.quick-bar {
    display: flex;
    align-items: center;
    gap: 12px;
    flex-wrap: wrap;
    margin-bottom: 12px;
}

.quick-search {
    width: 220px;
    max-width: 100%;
}

.quick-year {
    display: flex;
    align-items: center;
    gap: 6px;
}

.quick-year-label {
    min-width: 46px;
    text-align: center;
    font-size: var(--fs-label);
    font-weight: 700;
    color: var(--v-txtPrimary-base);
}

.icon-button {
    width: 26px;
    height: 26px;
    border-radius: 7px;
    background: var(--v-surface2-base);
    border: 1px solid var(--v-outline-base);
    color: var(--v-txtSecondary-base);
}

.icon-button:hover {
    color: var(--v-txtPrimary-base);
    border-color: var(--v-outline2-base);
}

.quick-legend {
    display: flex;
    align-items: center;
    gap: 12px;
    margin-left: auto;
}

.legend-item {
    display: flex;
    align-items: center;
    gap: 6px;
    font-size: 11px;
    color: var(--v-txtMuted-base);
}

.legend-dot {
    width: 10px;
    height: 10px;
    border-radius: 3px;
}

.legend-dot--paid {
    background: rgba(102, 204, 153, 0.4);
    border: 1px solid #66CC99;
}

.legend-dot--due {
    background: rgba(232, 92, 92, 0.25);
    border: 1px solid var(--v-error-base);
}

.quick-scroll {
    overflow-x: auto;
}

.quick-grid {
    display: grid;
    gap: 4px;
    min-width: 860px;
    align-items: stretch;
}

.quick-head {
    padding: 4px 0;
    font-size: 10px;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.04em;
    color: var(--v-txtMuted-base);
    text-align: center;
}

.quick-head--client {
    text-align: left;
}

.quick-head--current {
    color: var(--v-primary-base);
}

.quick-client {
    display: flex;
    align-items: center;
    gap: 8px;
    min-width: 0;
    padding-right: 8px;
}

.quick-avatar {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 26px;
    height: 26px;
    flex-shrink: 0;
    border-radius: 50%;
    font-size: 10px;
    font-weight: 700;
}

.quick-client-text {
    display: flex;
    flex-direction: column;
    min-width: 0;
}

.quick-client-name {
    font-size: var(--fs-label);
    font-weight: 600;
    color: var(--v-txtPrimary-base);
}

.quick-client-plan {
    font-size: 10px;
    color: var(--v-txtMuted-base);
}

.quick-cell {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 1px;
    min-height: 46px;
    border-radius: 6px;
    border: 1px solid var(--v-outline-base);
    background: var(--v-surface2-base);
    transition: border-color 0.15s, background 0.15s;
}

.quick-cell:hover {
    border-color: var(--v-outline2-base);
    background: var(--v-surface-base);
}

.cell-icon {
    font-size: 12px;
    font-weight: 700;
    line-height: 1;
}

.cell-icon--add {
    font-size: 18px;
    color: var(--v-outline2-base);
}

.cell-icon--muted {
    font-size: 16px;
    color: var(--v-outline2-base);
}

.cell-amount {
    font-size: 11px;
    font-weight: 700;
    line-height: 1.1;
}

.cell-method {
    font-size: 9px;
    line-height: 1;
    color: var(--v-txtMuted-base);
}

.quick-cell--paid {
    background: rgba(102, 204, 153, 0.18);
    border-color: rgba(102, 204, 153, 0.5);
    color: #66CC99;
}

.quick-cell--overdue {
    background: rgba(232, 92, 92, 0.12);
    border-color: rgba(232, 92, 92, 0.38);
    color: var(--v-error-base);
}

.quick-cell--future {
    cursor: default;
    opacity: 0.55;
}

.quick-cell--future:hover {
    border-color: var(--v-outline-base);
    background: var(--v-surface2-base);
}

.quick-total {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 1px;
    padding-top: 8px;
    margin-top: 4px;
    border-top: 1px solid var(--v-outline-base);
    font-size: 11px;
    color: var(--v-txtPrimary-base);
}

.quick-total--label {
    align-items: flex-start;
    font-size: 10px;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.04em;
    color: var(--v-txtMuted-base);
}

.quick-total small {
    font-size: 9px;
    color: var(--v-txtMuted-base);
}

.quick-truncated {
    padding: 12px 0 2px;
    text-align: center;
    font-size: 11px;
    color: var(--v-txtMuted-base);
}

.quick-empty {
    padding: 28px 0;
    text-align: center;
    font-size: var(--fs-caption);
    color: var(--v-txtMuted-base);
}
</style>
